/**
 * Custom hook for favorite/bookmark toggle animations
 * Provides smooth heart animation with celebration effects
 */

import { useCallback, useState } from 'react';
import { useSpring, useSpringValue } from '@react-spring/web';
import { useAnimationConfig, useReducedMotion } from './context';

interface UseFavoriteAnimationOptions {
  celebrationDuration?: number;
  bounceIntensity?: number;
  pulseCount?: number;
}

interface UseFavoriteAnimationReturn {
  springProps: {
    scale: any;
    rotate: any;
    opacity: any;
  };
  celebrationProps: {
    scale: any;
    opacity: any;
    rotate: any;
  };
  isAnimating: boolean;
  triggerFavorite: () => void;
  triggerUnfavorite: () => void;
}

export const useFavoriteAnimation = (
  options: UseFavoriteAnimationOptions = {}
): UseFavoriteAnimationReturn => {
  const {
    celebrationDuration = 0.6,
    bounceIntensity = 1.3,
    pulseCount = 2
  } = options;

  const [isAnimating, setIsAnimating] = useState(false);
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  // Spring values for main heart animation
  const scale = useSpringValue(1);
  const rotate = useSpringValue(0);
  const opacity = useSpringValue(1);

  // Spring values for celebration effect
  const celebrationScale = useSpringValue(1);
  const celebrationOpacity = useSpringValue(0);
  const celebrationRotate = useSpringValue(0);

  // Main heart animation spring
  const springProps = useSpring({
    scale: scale.get(),
    rotate: rotate.get(),
    opacity: opacity.get(),
    config: {
      tension: config.customTimings.springConfig.tension,
      friction: config.customTimings.springConfig.friction
    }
  });

  // Celebration effect spring
  const celebrationProps = useSpring({
    scale: celebrationScale.get(),
    opacity: celebrationOpacity.get(),
    rotate: celebrationRotate.get(),
    config: {
      tension: config.customTimings.springConfig.tension * 1.5,
      friction: config.customTimings.springConfig.friction * 0.8
    }
  });

  // Trigger favorite animation (adding to favorites)
  const triggerFavorite = useCallback(async () => {
    if (isAnimating || isReducedMotion) return;
    
    setIsAnimating(true);

    try {
      // Main heart bounce animation
      await Promise.all([
        scale.start({
          to: [
            { value: bounceIntensity, config: { duration: 150 } },
            { value: 1, config: { duration: 200 } }
          ]
        }),
        rotate.start({
          to: [
            { value: -10, config: { duration: 100 } },
            { value: 10, config: { duration: 100 } },
            { value: 0, config: { duration: 100 } }
          ]
        })
      ]);

      // Celebration effect
      celebrationScale.start({
        to: [
          { value: 1.5, config: { duration: celebrationDuration * 1000 * 0.3 } },
          { value: 0.8, config: { duration: celebrationDuration * 1000 * 0.7 } }
        ]
      });

      celebrationOpacity.start({
        to: [
          { value: 0.8, config: { duration: celebrationDuration * 1000 * 0.2 } },
          { value: 0, config: { duration: celebrationDuration * 1000 * 0.8 } }
        ]
      });

      celebrationRotate.start({
        to: { value: 360, config: { duration: celebrationDuration * 1000 } }
      });

      // Pulse effect
      for (let i = 0; i < pulseCount; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        scale.start({
          to: [
            { value: 1.1, config: { duration: 80 } },
            { value: 1, config: { duration: 80 } }
          ]
        });
      }

    } finally {
      setTimeout(() => {
        setIsAnimating(false);
        // Reset celebration values
        celebrationScale.set(1);
        celebrationOpacity.set(0);
        celebrationRotate.set(0);
      }, celebrationDuration * 1000);
    }
  }, [
    isAnimating,
    isReducedMotion,
    scale,
    rotate,
    celebrationScale,
    celebrationOpacity,
    celebrationRotate,
    bounceIntensity,
    celebrationDuration,
    pulseCount
  ]);

  // Trigger unfavorite animation (removing from favorites)
  const triggerUnfavorite = useCallback(async () => {
    if (isAnimating || isReducedMotion) return;
    
    setIsAnimating(true);

    try {
      // Shrink and fade animation
      await Promise.all([
        scale.start({
          to: [
            { value: 0.8, config: { duration: 150 } },
            { value: 1, config: { duration: 200 } }
          ]
        }),
        opacity.start({
          to: [
            { value: 0.5, config: { duration: 100 } },
            { value: 1, config: { duration: 150 } }
          ]
        }),
        rotate.start({
          to: [
            { value: 15, config: { duration: 125 } },
            { value: 0, config: { duration: 125 } }
          ]
        })
      ]);

    } finally {
      setTimeout(() => setIsAnimating(false), 250);
    }
  }, [isAnimating, isReducedMotion, scale, opacity, rotate]);

  // Return disabled animations for reduced motion
  if (isReducedMotion) {
    return {
      springProps: {
        scale: 1,
        rotate: 0,
        opacity: 1
      },
      celebrationProps: {
        scale: 1,
        opacity: 0,
        rotate: 0
      },
      isAnimating: false,
      triggerFavorite: () => {},
      triggerUnfavorite: () => {}
    };
  }

  return {
    springProps,
    celebrationProps,
    isAnimating,
    triggerFavorite,
    triggerUnfavorite
  };
};

/**
 * Simplified favorite animation hook with preset configurations
 */
export const useSimpleFavoriteAnimation = (intensity: 'subtle' | 'normal' | 'dramatic' = 'normal') => {
  const presets = {
    subtle: {
      bounceIntensity: 1.1,
      celebrationDuration: 0.4,
      pulseCount: 1
    },
    normal: {
      bounceIntensity: 1.3,
      celebrationDuration: 0.6,
      pulseCount: 2
    },
    dramatic: {
      bounceIntensity: 1.5,
      celebrationDuration: 0.8,
      pulseCount: 3
    }
  };

  return useFavoriteAnimation(presets[intensity]);
};